import React from 'react';
import { Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon from './Icon';
import ProgressBar from './ProgressBar';

const SHIPMENT_STAGES = ['Ordered', 'Shipped', 'In transit', 'Delivered'] as const;
export type ShipmentStage = (typeof SHIPMENT_STAGES)[number];

interface ShipmentMeterProps {
  /** Headline, e.g. "Arriving Wed, 04 Nov" or "On its way". */
  title: string;
  stage: ShipmentStage;
  /** Latest courier event, e.g. "Delhivery · Reached Pune hub, 02 Nov, 8:10 AM". */
  detail?: string;
  className?: string;
}

/** Delivery progress for an order (Figma "Shipment meter"). */
const ShipmentMeter: React.FC<ShipmentMeterProps> = ({ title, stage, detail, className }) => {
  const reached = SHIPMENT_STAGES.indexOf(stage);
  // The fill ends between the current stage and the next, matching the design.
  const progress = stage === 'Delivered' ? 1 : (reached + 0.6) / SHIPMENT_STAGES.length;
  return (
    <div className={cn('tw-flex tw-w-full tw-flex-col tw-gap-2.5 tw-rounded-xl tw-bg-ds-subtle tw-p-[14px]', className)}>
      <div className="tw-flex tw-items-center tw-gap-2.5">
        <Icon icon={Truck} className="tw-text-ds-info-fg" />
        <p className="tw-min-w-0 tw-flex-1 tw-text-[14px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{title}</p>
      </div>
      <ProgressBar value={progress} tone="info" label={`Shipment: ${stage}`} />
      <ol className="tw-flex tw-justify-between tw-gap-2 tw-text-[11px] tw-leading-[14px]">
        {SHIPMENT_STAGES.map((s, i) => (
          <li
            key={s}
            aria-current={i === reached ? 'step' : undefined}
            className={i <= reached ? 'tw-font-semibold tw-text-ds-text-primary' : 'tw-font-medium tw-text-ds-text-tertiary'}
          >
            {s}
          </li>
        ))}
      </ol>
      {detail && <p className="tw-text-[12px] tw-leading-4 tw-text-ds-text-tertiary">{detail}</p>}
    </div>
  );
};

export default ShipmentMeter;
